import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";
import { FormFooter } from "../FormFooter";
import { Icon } from "../../lib/icons";

const checkboxVariants = cva(
  "relative inline-flex items-center justify-center shrink-0 transition-all cursor-pointer box-border",
  {
    variants: {
      size: {
        small: "w-[10px] h-[10px] rounded-small border-[1.5px]",
        medium: "w-[12px] h-[12px] rounded-small border-[1.5px]",
        large: "w-[16px] h-[16px] rounded-medium border-[2px]",
      },
      validationState: {
        none: "",
        error:
          "border-action-outline-negative-default hover:border-action-outline-negative-hover focus:ring-2 ring-action-outline-negative-faded-hover",
      },
      isChecked: {
        true: "",
        false: "",
      },
      isIndeterminate: {
        true: "",
        false: "",
      },
      isDisabled: {
        true: "cursor-not-allowed opacity-60 border-action-outline-neutral-disabled bg-surface-fill-neutral-subtle",
        false: "",
      },
    },
    compoundVariants: [
      // Unchecked state - none validation
      {
        isChecked: false,
        validationState: "none",
        isDisabled: false,
        class:
          "border-action-outline-neutral-faded hover:bg-action-fill-neutral-faded hover:border-action-outline-neutral-faded",
      },
      // Checked state - none validation
      {
        isChecked: true,
        validationState: "none",
        isDisabled: false,
        class:
          "bg-action-fill-primary-default hover:bg-action-fill-primary-hover hover:border-action-fill-primary-hover border-action-fill-primary-default",
      },
      // Checked or Indeterminate state - error validation
      {
        isChecked: true,
        validationState: "error",
        isDisabled: false,
        class:
          "bg-action-fill-negative-default border-action-fill-negative-default hover:bg-action-fill-negative-hover hover:border-action-fill-negative-hover",
      },
      // Indeterminate state - none validation
      {
        isIndeterminate: true,
        validationState: "none",
        isDisabled: false,
        class:
          "bg-action-fill-primary-default border-action-fill-primary-default hover:bg-action-fill-primary-hover hover:border-action-fill-primary-hover",
      },
      // Indeterminate state - error validation (same as checked error)
      {
        isIndeterminate: true,
        validationState: "error",
        isDisabled: false,
        class:
          "bg-action-fill-negative-default border-action-fill-negative-default hover:bg-action-fill-negative-hover hover:border-action-fill-negative-hover",
      },
    ],
    defaultVariants: {
      size: "medium",
      validationState: "none",
      isChecked: false,
      isIndeterminate: false,
      isDisabled: false,
    },
  }
);

export interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  /**
   * Label text to display next to the checkbox
   */
  label?: string;
  /**
   * Error text to display below the checkbox
   */
  errorText?: string;
  /**
   * Size of the checkbox
   */
  size?: "small" | "medium" | "large";
  /**
   * Validation state of the checkbox
   */
  validationState?: "none" | "error";
  /**
   * Whether the checkbox is disabled
   */
  isDisabled?: boolean;
  /**
   * Whether the checkbox is in an indeterminate state
   */
  isIndeterminate?: boolean;
  /**
   * Whether to show error text (defaults to true if errorText is provided)
   */
  showErrorText?: boolean;
  /**
   * Custom class name for the container
   */
  containerClassName?: string;
  /**
   * Custom class name for the label
   */
  labelClassName?: string;
}

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      errorText,
      size = "medium",
      validationState = "none",
      isDisabled = false,
      isIndeterminate = false,
      showErrorText = true,
      containerClassName,
      labelClassName,
      className,
      checked,
      onChange,
      ...props
    },
    ref
  ) => {
    const [internalChecked, setInternalChecked] = React.useState(false);
    const [showRipple, setShowRipple] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const rippleTimeoutRef = React.useRef<number | null>(null);

    // Use forwarded ref or internal ref
    React.useImperativeHandle(ref, () => inputRef.current!);

    const isChecked = checked !== undefined ? checked : internalChecked;

    // Set indeterminate property on the input element
    React.useEffect(() => {
      if (inputRef.current) {
        inputRef.current.indeterminate = isIndeterminate;
      }
    }, [isIndeterminate]);

    // Cleanup timeout on unmount
    React.useEffect(() => {
      return () => {
        if (rippleTimeoutRef.current) {
          clearTimeout(rippleTimeoutRef.current);
        }
      };
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(e);
      } else {
        setInternalChecked(e.target.checked);
      }
    };

    const triggerRipple = () => {
      if (!isDisabled && !showRipple) {
        // Clear any existing timeout
        if (rippleTimeoutRef.current) {
          clearTimeout(rippleTimeoutRef.current);
        }

        setShowRipple(true);
        rippleTimeoutRef.current = setTimeout(() => {
          setShowRipple(false);
          rippleTimeoutRef.current = null;
        }, 400); // Match animation duration (0.4s)
      }
    };

    const handleContainerClick = () => {
      if (!isDisabled && inputRef.current) {
        triggerRipple();
        inputRef.current.click();
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if ((e.key === " " || e.key === "Enter") && !isDisabled) {
        e.preventDefault();
        triggerRipple();
        inputRef.current?.click();
      }
    };

    // Size-based configurations
    const sizeConfig = {
      small: {
        gap: "gap-2",
        labelSize: "text-body-small-regular",
        iconSize: 10,
      },
      medium: {
        gap: "gap-2",
        labelSize: "text-body-small-regular",
        iconSize: 12,
      },
      large: {
        gap: "gap-3",
        labelSize: "text-body-medium-regular",
        iconSize: 14,
      },
    };

    const config = sizeConfig[size];

    // Determine if we should show the error text
    const shouldShowError = errorText && showErrorText;

    return (
      <div className={cn("inline-flex flex-col px-2", containerClassName)}>
        <div
          className={cn(
            "inline-flex items-center",
            config.gap,
            isDisabled ? "cursor-not-allowed" : "cursor-pointer"
          )}
          onClick={handleContainerClick}
          onKeyDown={handleKeyDown}
          role="checkbox"
          aria-checked={isIndeterminate ? "mixed" : isChecked}
          aria-disabled={isDisabled}
          tabIndex={isDisabled ? -1 : 0}
        >
          <input
            ref={inputRef}
            type="checkbox"
            className="sr-only"
            checked={isChecked}
            onChange={handleChange}
            disabled={isDisabled}
            {...props}
          />

          <div className="relative inline-flex shrink-0">
            {/* Ripple Effect */}
            {showRipple && (
              <div
                className={cn(
                  "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-none w-full h-full",
                  validationState === "error"
                    ? "bg-action-outline-negative-faded"
                    : "bg-action-outline-primary-faded"
                )}
                style={{
                  animation: "var(--animate-checkbox-ripple)",
                }}
              />
            )}

            <div
              className={cn(
                checkboxVariants({
                  size,
                  validationState,
                  isChecked: isChecked && !isIndeterminate,
                  isIndeterminate,
                  isDisabled,
                }),
                className
              )}
            >
              {/* Checkmark Icon */}
              {isChecked && !isIndeterminate && (
                <Icon
                  name="tick"
                  size={config.iconSize}
                  className="text-action-ink-on-primary-normal"
                />
              )}

              {/* Indeterminate Icon */}
              {isIndeterminate && (
                <svg
                  width={config.iconSize}
                  height={config.iconSize}
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-action-ink-on-primary-normal"
                >
                  <path
                    d="M3 6H9"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              )}
            </div>
          </div>

          {label && (
            <label
              className={cn(
                config.labelSize,
                "select-none inline-flex items-center",
                isDisabled
                  ? "text-surface-ink-neutral-disabled"
                  : "text-surface-ink-neutral-normal",
                labelClassName
              )}
            >
              {label}
            </label>
          )}
        </div>

        {/* Error text */}
        {shouldShowError && (
          <FormFooter
            helperText={errorText}
            validationState="negative"
            size={size}
            isDisabled={isDisabled}
          />
        )}
      </div>
    );
  }
);

Checkbox.displayName = "Checkbox";

export { Checkbox, checkboxVariants };
